package com.rosario.juridico.domain

import juridico5.composeapp.generated.resources.Res
import kotlinx.datetime.*
import kotlin.math.pow

// -----------------------------------------------------------------------------
// MODELOS DE DOMINIO — Intereses
// -----------------------------------------------------------------------------

data class TrasoPeriodo(
    val desde: LocalDate,
    val hasta: LocalDate,
    val dias: Int,
    val tasaAnual: Double,
    val tasaPeriodo: Double,
    val baseCalculo: Double,
    val interesParcial: Double
)

data class ResultadoLiquidacion(
    val capital: Double,
    val interesTotal: Double,
    val totalAdeudado: Double,
    val diasTotal: Long,
    val tasaEfectiva: Double,
    val periodos: List<TrasoPeriodo>,
    val nombreTasa: String,
    val tipoCapitalizacion: String
)

data class PeriodoTasa(
    val desde: LocalDate,
    val hasta: LocalDate,
    val tasaAnual: Double
)

enum class TipoTasa(val etiqueta: String, val periodos: List<PeriodoTasa>) {
    BNA_ACTIVA(
        etiqueta = "Tasa activa — Banco Nación",
        periodos = listOf(
            PeriodoTasa(LocalDate(2019, 1, 1),  LocalDate(2019, 12, 31), 0.6095),
            PeriodoTasa(LocalDate(2020, 1, 1),  LocalDate(2020, 12, 31), 0.4378),
            PeriodoTasa(LocalDate(2021, 1, 1),  LocalDate(2021, 12, 31), 0.3695),
            PeriodoTasa(LocalDate(2022, 1, 1),  LocalDate(2022, 6, 30),  0.4200),
            PeriodoTasa(LocalDate(2022, 7, 1),  LocalDate(2022, 12, 31), 0.6675),
            PeriodoTasa(LocalDate(2023, 1, 1),  LocalDate(2023, 6, 30),  0.8763),
            PeriodoTasa(LocalDate(2023, 7, 1),  LocalDate(2023, 12, 31), 1.3298),
            PeriodoTasa(LocalDate(2024, 1, 1),  LocalDate(2024, 6, 30),  1.5210),
            PeriodoTasa(LocalDate(2024, 7, 1),  LocalDate(2025, 6, 30),  0.8640),
            PeriodoTasa(LocalDate(2025, 7, 1),  LocalDate(2025, 12, 31), 0.6500)
        )
    ),
    BNA_PASIVA(
        etiqueta = "Tasa pasiva — Banco Nación",
        periodos = listOf(
            PeriodoTasa(LocalDate(2019, 1, 1),  LocalDate(2019, 12, 31), 0.2990),
            PeriodoTasa(LocalDate(2020, 1, 1),  LocalDate(2020, 12, 31), 0.2750),
            PeriodoTasa(LocalDate(2021, 1, 1),  LocalDate(2021, 12, 31), 0.2100),
            PeriodoTasa(LocalDate(2022, 1, 1),  LocalDate(2022, 12, 31), 0.3500),
            PeriodoTasa(LocalDate(2023, 1, 1),  LocalDate(2023, 6, 30),  0.6900),
            PeriodoTasa(LocalDate(2023, 7, 1),  LocalDate(2023, 12, 31), 1.1000),
            PeriodoTasa(LocalDate(2024, 1, 1),  LocalDate(2025, 6, 30),  0.6500),
            PeriodoTasa(LocalDate(2025, 7, 1),  LocalDate(2025, 12, 31), 0.4800)
        )
    ),
    BCRA_REFERENCIA(
        etiqueta = "Tasa referencia — BCRA",
        periodos = listOf(
            PeriodoTasa(LocalDate(2019, 1, 1),  LocalDate(2019, 12, 31), 0.6000),
            PeriodoTasa(LocalDate(2020, 1, 1),  LocalDate(2020, 12, 31), 0.3800),
            PeriodoTasa(LocalDate(2021, 1, 1),  LocalDate(2021, 12, 31), 0.3800),
            PeriodoTasa(LocalDate(2022, 1, 1),  LocalDate(2022, 12, 31), 0.6625),
            PeriodoTasa(LocalDate(2023, 1, 1),  LocalDate(2023, 12, 31), 1.0000),
            PeriodoTasa(LocalDate(2024, 1, 1),  LocalDate(2025, 6, 30),  1.1000),
            PeriodoTasa(LocalDate(2025, 7, 1),  LocalDate(2025, 12, 31), 0.7200)
        )
    ),
    NB_SANTA_FE(
        etiqueta = "Tasa activa — NB Santa Fe",
        periodos = listOf(
            PeriodoTasa(LocalDate(2019, 1, 1),  LocalDate(2019, 12, 31), 0.6250),
            PeriodoTasa(LocalDate(2020, 1, 1),  LocalDate(2020, 12, 31), 0.4520),
            PeriodoTasa(LocalDate(2021, 1, 1),  LocalDate(2021, 12, 31), 0.3850),
            PeriodoTasa(LocalDate(2022, 1, 1),  LocalDate(2022, 12, 31), 0.6950),
            PeriodoTasa(LocalDate(2023, 1, 1),  LocalDate(2023, 12, 31), 1.1500),
            PeriodoTasa(LocalDate(2024, 1, 1),  LocalDate(2024, 6, 30),  1.4800),
            PeriodoTasa(LocalDate(2024, 7, 1),  LocalDate(2025, 12, 31), 0.8200)
        )
    ),
    BADLAR(
        etiqueta = "Tasa BADLAR",
        periodos = listOf(
            PeriodoTasa(LocalDate(2019, 1, 1),  LocalDate(2019, 12, 31), 0.4850),
            PeriodoTasa(LocalDate(2020, 1, 1),  LocalDate(2020, 12, 31), 0.2980),
            PeriodoTasa(LocalDate(2021, 1, 1),  LocalDate(2021, 12, 31), 0.3410),
            PeriodoTasa(LocalDate(2022, 1, 1),  LocalDate(2022, 12, 31), 0.5260),
            PeriodoTasa(LocalDate(2023, 1, 1),  LocalDate(2023, 12, 31), 0.9500),
            PeriodoTasa(LocalDate(2024, 1, 1),  LocalDate(2025, 12, 31), 0.7100)
        )
    ),
    UVA(
        etiqueta = "Ajuste UVA + 1% anual",
        periodos = listOf(
            PeriodoTasa(LocalDate(2019, 1, 1),  LocalDate(2019, 12, 31), 0.5350),
            PeriodoTasa(LocalDate(2020, 1, 1),  LocalDate(2020, 12, 31), 0.3620),
            PeriodoTasa(LocalDate(2021, 1, 1),  LocalDate(2021, 12, 31), 0.5090),
            PeriodoTasa(LocalDate(2022, 1, 1),  LocalDate(2022, 12, 31), 0.7280),
            PeriodoTasa(LocalDate(2023, 1, 1),  LocalDate(2023, 12, 31), 2.1100),
            PeriodoTasa(LocalDate(2024, 1, 1),  LocalDate(2025, 6, 30),  1.1800),
            PeriodoTasa(LocalDate(2025, 7, 1),  LocalDate(2025, 12, 31), 0.6500)
        )
    ),
    CAC(
        etiqueta = "Índice CAC (construcción)",
        periodos = listOf(
            PeriodoTasa(LocalDate(2019, 1, 1),  LocalDate(2019, 12, 31), 0.5500),
            PeriodoTasa(LocalDate(2020, 1, 1),  LocalDate(2020, 12, 31), 0.4200),
            PeriodoTasa(LocalDate(2021, 1, 1),  LocalDate(2021, 12, 31), 0.6800),
            PeriodoTasa(LocalDate(2022, 1, 1),  LocalDate(2022, 12, 31), 0.9500),
            PeriodoTasa(LocalDate(2023, 1, 1),  LocalDate(2023, 12, 31), 1.8500),
            PeriodoTasa(LocalDate(2024, 1, 1),  LocalDate(2025, 6, 30),  1.4200),
            PeriodoTasa(LocalDate(2025, 7, 1),  LocalDate(2025, 12, 31), 0.7500)
        )
    )
}

enum class TipoCapitalizacion(val etiqueta: String) {
    SIMPLE("Simple"),
    COMPUESTO("Compuesto")
}

// -----------------------------------------------------------------------------
// MODELOS DE DOMINIO — IPC y Salarios
// -----------------------------------------------------------------------------

data class ResultadoIPC(
    val montoInicial: Double,
    val montoActualizado: Double,
    val variacionPorcentual: Double,
    val indiceInicial: Double,
    val indiceFinal: Double,
    val mesesTranscurridos: Long,
    val esProyectado: Boolean = false,
    val mesUsadoComoProyeccion: String? = null,
    val mesSolicitado: String? = null,
    val mesRealUtilizado: String? = null
)

data class DatoSalario(val anio: Int, val mes: Int, val convenio: String, val indice: Double)

data class ResultadoSalario(
    val montoInicial: Double,
    val montoActualizado: Double,
    val variacionPorcentual: Double,
    val indiceInicial: Double,
    val indiceFinal: Double,
    val mesesTranscurridos: Long,
    val convenio: String
)

// -----------------------------------------------------------------------------
// LOGICA DE CÁLCULO DE DÍAS Y CACHÉ
// -----------------------------------------------------------------------------

private var _cacheFeriados: Set<LocalDate>? = null

suspend fun cargarFeriados(): Set<LocalDate> {
    _cacheFeriados?.let { return it }
    return try {
        val texto = Res.readBytes("files/feriados_argentina.txt").decodeToString()
        val feriados = mutableSetOf<LocalDate>()
        texto.lines().forEach { linea ->
            if (linea.isNotBlank() && !linea.startsWith("#")) {
                try {
                    val fechaStr = linea.split("#")[0].trim()
                    if (fechaStr.isNotBlank()) feriados.add(LocalDate.parse(fechaStr))
                } catch (e: Exception) { }
            }
        }
        _cacheFeriados = feriados
        feriados
    } catch (e: Exception) {
        emptySet()
    }
}

fun esDiaHabilJudicial(fecha: LocalDate, feriados: Set<LocalDate>): Boolean {
    if (fecha.dayOfWeek == DayOfWeek.SATURDAY || fecha.dayOfWeek == DayOfWeek.SUNDAY) return false
    if (feriados.contains(fecha)) return false
    if (fecha.month == Month.JANUARY) return false
    val inicioFeria = LocalDate(fecha.year, 7, 15)
    val finFeria    = LocalDate(fecha.year, 7, 26)
    if (fecha >= inicioFeria && fecha <= finFeria) return false
    if (fecha.month == Month.NOVEMBER && fecha.dayOfMonth == 16) return false
    return true
}

fun calcularDiasHabilesJudiciales(inicio: LocalDate, fin: LocalDate, feriados: Set<LocalDate>): Int {
    if (inicio > fin) return 0
    var dias = 0
    var fecha = inicio
    while (fecha <= fin) {
        if (esDiaHabilJudicial(fecha, feriados)) dias++
        fecha = fecha.plus(1, DateTimeUnit.DAY)
    }
    return dias
}

fun calcularFechaFin(fechaInicio: LocalDate, diasHabiles: Int, feriados: Set<LocalDate>): LocalDate {
    var fecha = fechaInicio
    var sumados = 0
    while (sumados < diasHabiles) {
        fecha = fecha.plus(1, DateTimeUnit.DAY)
        if (esDiaHabilJudicial(fecha, feriados)) sumados++
    }
    return fecha
}

fun calcularDiasCorridos(inicio: LocalDate, fin: LocalDate): Int {
    if (inicio > fin) return 0
    return inicio.until(fin, DateTimeUnit.DAY)
}

// ─────────────────────────────────────────────────────────────────────────────
// MOTORES DE CÁLCULO ECONÓMICO
// ─────────────────────────────────────────────────────────────────────────────

fun calcularIntereses(
    capital: Double,
    fechaInicio: LocalDate,
    fechaFin: LocalDate,
    tipoTasa: TipoTasa,
    capitalizacion: TipoCapitalizacion
): ResultadoLiquidacion? {
    if (capital <= 0 || fechaInicio >= fechaFin) return null

    val diasTotal = fechaInicio.until(fechaFin, DateTimeUnit.DAY).toLong()
    val filas = mutableListOf<TrasoPeriodo>()
    var cursor = fechaInicio
    var baseActual = capital
    var interesTotal = 0.0

    while (cursor < fechaFin) {
        val segmento = tipoTasa.periodos.firstOrNull { p ->
            cursor >= p.desde && cursor <= p.hasta
        } ?: break

        val finSegmento = if (segmento.hasta < fechaFin) {
            segmento.hasta.plus(1, DateTimeUnit.DAY)
        } else {
            fechaFin
        }
        
        val dias = cursor.until(finSegmento, DateTimeUnit.DAY)
        val tasaDiaria = segmento.tasaAnual / 365.0
        val tasaPeriodo = tasaDiaria * dias
        val interesParcial = baseActual * tasaPeriodo

        filas.add(
            TrasoPeriodo(
                desde = cursor,
                hasta = finSegmento.plus(-1, DateTimeUnit.DAY),
                dias = dias,
                tasaAnual = segmento.tasaAnual,
                tasaPeriodo = tasaPeriodo,
                baseCalculo = baseActual,
                interesParcial = interesParcial
            )
        )

        interesTotal += interesParcial
        if (capitalizacion == TipoCapitalizacion.COMPUESTO) baseActual += interesParcial
        cursor = finSegmento
    }

    return ResultadoLiquidacion(
        capital = capital,
        interesTotal = interesTotal,
        totalAdeudado = capital + interesTotal,
        diasTotal = diasTotal,
        tasaEfectiva = if (capital > 0) interesTotal / capital else 0.0,
        periodos = filas,
        nombreTasa = tipoTasa.etiqueta,
        tipoCapitalizacion = capitalizacion.etiqueta
    )
}

/**
 * Calcula el IPC aplicando la Regla Pericial:
 * 1. Desplazamiento: Se usa el índice del mes anterior a la fecha ingresada.
 * 2. Proyección: Si el mes solicitado no existe, proyecta usando la última tasa conocida.
 */
fun calcularIPCPericial(
    montoInicial: Double,
    fechaInicio: LocalDate,
    fechaFin: LocalDate,
    indices: Map<String, Double>
): ResultadoIPC? {
    if (montoInicial <= 0 || indices.isEmpty()) return null

    // Función auxiliar para obtener la fecha del mes anterior
    fun obtenerMesVencido(fecha: LocalDate): LocalDate {
        var mes = fecha.monthNumber - 1
        var anio = fecha.year
        if (mes == 0) {
            mes = 12
            anio -= 1
        }
        return LocalDate(anio, mes, 1)
    }

    fun toKey(fecha: LocalDate) = "${fecha.year}-${fecha.monthNumber.toString().padStart(2, '0')}"

    // 1. Obtener índice base (Mes vencido a la fecha de inicio)
    val fechaBaseEfectiva = obtenerMesVencido(fechaInicio)
    val claveBase = toKey(fechaBaseEfectiva)
    
    // Si no existe el índice base (fecha muy antigua), buscamos el primero disponible
    val indiceInicio = indices[claveBase] ?: indices.values.firstOrNull() ?: 1.0

    // 2. Obtener índice destino (Lógica de Proyección)
    val fechaDestinoRequerida = obtenerMesVencido(fechaFin)
    val claveDestinoSolicitada = toKey(fechaDestinoRequerida)

    val ultimaClaveOficial = indices.keys.maxOrNull() ?: return null
    val ultimoIndiceOficial = indices[ultimaClaveOficial] ?: 1.0

    var indiceFinalEfectivo: Double
    var esProyectado = false
    var mesRealUtilizado = ultimaClaveOficial

    if (claveDestinoSolicitada <= ultimaClaveOficial) {
        // Caso A: El índice ya existe o es anterior
        indiceFinalEfectivo = indices[claveDestinoSolicitada] ?: ultimoIndiceOficial
        mesRealUtilizado = if (indices.containsKey(claveDestinoSolicitada)) claveDestinoSolicitada else ultimaClaveOficial
    } else {
        // Caso B: Proyección Geométrica
        esProyectado = true

        // Calculamos tasa del último mes disponible
        val clavesOrdenadas = indices.keys.sorted()
        val penultimaClave = clavesOrdenadas.getOrNull(clavesOrdenadas.size - 2)
        val penultimoIndice = indices[penultimaClave] ?: (ultimoIndiceOficial / 1.04)

        val tasaUltimoMes = ultimoIndiceOficial / penultimoIndice

        // Calcular cuántos meses faltan desde el último oficial hasta el solicitado
        val anioOficial = ultimaClaveOficial.take(4).toInt()
        val mesOficial = ultimaClaveOficial.takeLast(2).toInt()
        val mesesFaltantes = (fechaDestinoRequerida.year - anioOficial) * 12 + (fechaDestinoRequerida.monthNumber - mesOficial)

        // Interés Compuesto: Vf = Vi * (1 + r)^n
        indiceFinalEfectivo = ultimoIndiceOficial * tasaUltimoMes.pow(mesesFaltantes.toDouble())
    }

    val montoActualizado = montoInicial * (indiceFinalEfectivo / indiceInicio)
    val variacion = ((indiceFinalEfectivo / indiceInicio) - 1.0) * 100.0

    // Conteo de meses calendarios entre fechas
    val mesesTranscurridos = ((fechaFin.year - fechaInicio.year) * 12 + (fechaFin.monthNumber - fechaInicio.monthNumber)).toLong()

    return ResultadoIPC(
        montoInicial = montoInicial,
        montoActualizado = montoActualizado,
        variacionPorcentual = variacion,
        indiceInicial = indiceInicio,
        indiceFinal = indiceFinalEfectivo,
        mesesTranscurridos = mesesTranscurridos,
        esProyectado = esProyectado,
        mesUsadoComoProyeccion = if (esProyectado) ultimaClaveOficial else null,
        mesSolicitado = claveDestinoSolicitada,
        mesRealUtilizado = mesRealUtilizado
    )
}

/**
 * Calcula el ICL (Índice para Contratos de Locación) diario.
 */
fun calcularICLDiario(
    montoInicial: Double,
    fechaInicio: LocalDate,
    fechaFin: LocalDate,
    indices: Map<String, Double>
): ResultadoIPC? {
    if (montoInicial <= 0 || indices.isEmpty()) return null

    fun LocalDate.toIclKey() = "${this.year}-${this.monthNumber.toString().padStart(2, '0')}-${this.dayOfMonth.toString().padStart(2, '0')}"

    val claveInicio = fechaInicio.toIclKey()
    val indiceInicio = indices[claveInicio] ?: run {
        // Si no existe el inicio exacto, buscar el más cercano anterior
        val minClave = indices.keys.minOrNull() ?: ""
        var actual = fechaInicio
        while (!indices.containsKey(actual.toIclKey()) && actual.toIclKey() >= minClave) {
            actual = actual.minus(1, DateTimeUnit.DAY)
        }
        indices[actual.toIclKey()] ?: 1.0
    }

    var actualFin = fechaFin
    var claveFin = actualFin.toIclKey()
    var esProyectado = false
    val minClave = indices.keys.minOrNull() ?: ""

    // Backtracking diario si no existe el índice de la fecha de fin
    while (!indices.containsKey(claveFin) && claveFin >= minClave) {
        esProyectado = true
        actualFin = actualFin.minus(1, DateTimeUnit.DAY)
        claveFin = actualFin.toIclKey()
    }

    val indiceFin = indices[claveFin] ?: 1.0

    val montoActualizado = montoInicial * (indiceFin / indiceInicio)
    val variacion = ((indiceFin / indiceInicio) - 1.0) * 100.0
    val meses = ((fechaFin.year - fechaInicio.year) * 12 + (fechaFin.monthNumber - fechaInicio.monthNumber)).toLong()

    return ResultadoIPC(
        montoInicial = montoInicial,
        montoActualizado = montoActualizado,
        variacionPorcentual = variacion,
        indiceInicial = indiceInicio,
        indiceFinal = indiceFin,
        mesesTranscurridos = meses,
        esProyectado = esProyectado,
        mesUsadoComoProyeccion = if (esProyectado) claveFin else null
    )
}

object DatosSalario {
    val tabla: List<DatoSalario> = listOf(
        DatoSalario(2017, 1, "IS_TOTAL", 115.8), DatoSalario(2017, 12, "IS_TOTAL", 153.4),
        DatoSalario(2018, 1, "IS_TOTAL", 158.0), DatoSalario(2018, 12, "IS_TOTAL", 258.5),
        DatoSalario(2024, 1, "IS_TOTAL", 11423.7), DatoSalario(2024, 12, "IS_TOTAL", 27314.6),
        
        // Comercio (CCT 130/75) - Ejemplos
        DatoSalario(2024, 1, "CCT_COMERCIO", 15000.0), DatoSalario(2024, 12, "CCT_COMERCIO", 28000.0),
        // UOCRA (Construcción) - Ejemplos
        DatoSalario(2024, 1, "CCT_UOCRA", 14500.0), DatoSalario(2024, 12, "CCT_UOCRA", 26500.0),
        // Sanidad - Ejemplos
        DatoSalario(2024, 1, "CCT_SANIDAD", 16000.0), DatoSalario(2024, 12, "CCT_SANIDAD", 30000.0)
    )
    
    val convenios = listOf(
        "IS_TOTAL" to "Índice de Salarios Total (INDEC)",
        "CCT_COMERCIO" to "Empleados de Comercio (CCT 130/75)",
        "CCT_UOCRA" to "UOCRA - Construcción",
        "CCT_SANIDAD" to "Sanidad (ATSA)",
        "CCT_UOM" to "UOM - Metalúrgicos",
        "CCT_GASTRONOMICOS" to "Gastronómicos (UTHGRA)"
    )

    fun etiqueta(clave: String) = convenios.firstOrNull { it.first == clave }?.second ?: clave
    fun obtener(anio: Int, mes: Int, convenio: String): DatoSalario? {
        val serie = tabla.filter { it.convenio == convenio }.sortedBy { it.anio * 12 + it.mes }
        if (serie.isEmpty()) return null
        val exacto = serie.firstOrNull { it.anio == anio && it.mes == mes }
        if (exacto != null) return exacto
        val objetivo = anio * 12 + mes
        val anterior = serie.lastOrNull { it.anio * 12 + it.mes < objetivo }
        val siguiente = serie.firstOrNull { it.anio * 12 + it.mes > objetivo }
        return when {
            anterior == null -> siguiente
            siguiente == null -> anterior
            else -> {
                val totalMeses = (siguiente.anio * 12 + siguiente.mes) - (anterior.anio * 12 + anterior.mes)
                val delta = objetivo - (anterior.anio * 12 + anterior.mes)
                val fraccion = delta.toDouble() / totalMeses
                DatoSalario(anio, mes, convenio, anterior.indice + fraccion * (siguiente.indice - anterior.indice))
            }
        }
    }
    fun anioMin() = tabla.minOf { it.anio }
    fun anioMax() = tabla.maxOf { it.anio }
}

fun calcularSalario(montoInicial: Double, fechaInicio: LocalDate, fechaFin: LocalDate, convenio: String): ResultadoSalario? {
    if (montoInicial <= 0 || fechaInicio >= fechaFin) return null
    val datoInicio = DatosSalario.obtener(fechaInicio.year, fechaInicio.monthNumber, convenio) ?: return null
    val datoFin    = DatosSalario.obtener(fechaFin.year,    fechaFin.monthNumber,    convenio) ?: return null
    if (datoInicio.indice <= 0) return null
    val montoActualizado = montoInicial * (datoFin.indice / datoInicio.indice)
    val variacion = ((datoFin.indice / datoInicio.indice) - 1.0) * 100.0
    val meses = ((fechaFin.year - fechaInicio.year) * 12 + (fechaFin.monthNumber - fechaInicio.monthNumber)).toLong()
    return ResultadoSalario(montoInicial, montoActualizado, variacion, datoInicio.indice, datoFin.indice, meses, convenio)
}

// ─────────────────────────────────────────────────────────────────────────────
// CALCULADORA CAC — Cámara Argentina de la Construcción
// ─────────────────────────────────────────────────────────────────────────────

/** Estado de un índice: publicado oficialmente o estimado. */
enum class EstadoIndiceCAC { PUBLICADO, ESTIMADO }

/** Resultado de un componente individual del cálculo CAC. */
data class ResultadoComponenteCAC(
    val nombre: String,
    val indiceOrigen: Double,
    val indiceDestino: Double,
    val montoActualizado: Double,
    val diferencia: Double,
    val variacionPorcentual: Double,
    val estadoOrigen: EstadoIndiceCAC,
    val estadoDestino: EstadoIndiceCAC
)

/** Resultado completo del cálculo CAC con los tres componentes. */
data class ResultadoCAC(
    val montoBase: Double,
    val general: ResultadoComponenteCAC,
    val materiales: ResultadoComponenteCAC,
    val manoObra: ResultadoComponenteCAC,
    /** Mes pendiente de publicación oficial (null si todos están publicados). */
    val mesPendiente: String? = null,
    /** Fecha estimada de próxima publicación en formato dd/mm/aaaa. */
    val fechaProximaPublicacion: String? = null,
    val mesSolicitado: String? = null,
    val mesRealUtilizado: String? = null
)

/** Avanza una clave "AAAA-MM" al mes siguiente. */
fun siguienteMesCAC(fecha: String): String {
    val partes = fecha.split("-")
    if (partes.size != 2) return fecha
    var anio = partes[0].toInt()
    var mes  = partes[1].toInt()
    mes++
    if (mes > 12) { mes = 1; anio++ }
    return "$anio-${mes.toString().padStart(2, '0')}"
}

/**
 * Calcula la actualización por índice CAC utilizando datos de la nube.
 */
fun calcularCAC(
    montoBase: Double,
    claveOrigen: String,
    claveDestino: String,
    datos: List<com.rosario.juridico.platform.IndiceCac>
): ResultadoCAC? {
    if (montoBase <= 0 || datos.isEmpty()) return null
    if (claveOrigen >= claveDestino) return null

    // Normalizamos claves a "YYYY-MM" para búsqueda
    val startKey = claveOrigen.take(7)
    val endKey = claveDestino.take(7)

    // Obtener o estimar índice origen
    val datoOrigen = datos.filter { it.fecha.startsWith(startKey) }.firstOrNull()
        ?: datos.filter { it.fecha < startKey }.maxByOrNull { it.fecha }
        ?: datos.first()

    // Obtener o estimar índice destino
    val datoDestino = datos.filter { it.fecha.startsWith(endKey) }.firstOrNull()
        ?: datos.filter { it.fecha < endKey }.maxByOrNull { it.fecha }
        ?: datos.last()

    val esProyectado = !datos.any { it.fecha.startsWith(endKey) }
    val mesRealUtilizado = datoDestino.fecha.take(7)

    fun componente(nombre: String, vOrig: Double, vDest: Double): ResultadoComponenteCAC {
        val actualizado = montoBase * (vDest / vOrig)
        return ResultadoComponenteCAC(
            nombre = nombre,
            indiceOrigen = vOrig,
            indiceDestino = vDest,
            montoActualizado = actualizado,
            diferencia = actualizado - montoBase,
            variacionPorcentual = ((vDest / vOrig) - 1.0) * 100.0,
            estadoOrigen = EstadoIndiceCAC.PUBLICADO,
            estadoDestino = if (esProyectado) EstadoIndiceCAC.ESTIMADO else EstadoIndiceCAC.PUBLICADO
        )
    }

    // Lógica de fecha de publicación
    val ultimoPublicado = datos.maxByOrNull { it.fecha }?.fecha?.take(7)
    val mesPendiente = if (esProyectado && ultimoPublicado != null) siguienteMesCAC(ultimoPublicado) else null
    val fechaProxima = mesPendiente?.let { mp ->
        val p = mp.split("-")
        val a = p[0].toInt(); val m = p[1].toInt()
        val mpab = if (m == 12) 1 else m + 1
        val apab = if (m == 12) a + 1 else a
        "18/${mpab.toString().padStart(2, '0')}/$apab"
    }

    return ResultadoCAC(
        montoBase = montoBase,
        general = componente("General", datoOrigen.general, datoDestino.general),
        materiales = componente("Materiales", datoOrigen.materiales, datoDestino.materiales),
        manoObra = componente("Mano de Obra", datoOrigen.manoObra, datoDestino.manoObra),
        mesPendiente = mesPendiente,
        fechaProximaPublicacion = fechaProxima,
        mesSolicitado = endKey,
        mesRealUtilizado = mesRealUtilizado
    )
}

// ─────────────────────────────────────────────────────────────────────────────
// CALCULADORA JUS SANTA FE (Art. 32 Ley 12851)
// ─────────────────────────────────────────────────────────────────────────────

data class DatoJus(
    val fecha: LocalDate,
    val valor: Double
)

data class ResultadoJus(
    val cantidadJus: Double,
    val valorUnitarioHistorico: Double,
    val valorUnitarioActual: Double,
    val montoHistoricoPesos: Double,
    val montoActualizadoPesos: Double,
    val fechaHistorica: LocalDate,
    val fechaActual: LocalDate
)

suspend fun leerJusBase(): String {
    return try {
        Res.readBytes("files/valor_jus_base.txt").decodeToString()
    } catch (e: Exception) { "" }
}

private var _cacheJus: List<DatoJus>? = null

suspend fun obtenerDatosJus(): List<DatoJus> {
    _cacheJus?.let { return it }
    val texto = leerJusBase()
    _cacheJus = parsearJus(texto)
    return _cacheJus ?: emptyList()
}

/**
 * Parsea el archivo de valores JUS.
 * El archivo tiene un formato mixto pero predominantemente:
 * "$VALOR a partir del 1 de MES de AÑO" o "$VALOR a partir del D.M.AAAA"
 */
fun parsearJus(texto: String): List<DatoJus> {
    // Regex mejorada para capturar montos y fechas con variaciones de espacios y formatos
    val regex = Regex("""\$?\s*(\d+(?:\.\d+)*,\s*\d{2}).*?(\d{1,2}[\./]\d{1,2}[\./]\d{2,4}|1 de \w+ de \d{4})""")
    val meses = mapOf(
        "enero" to 1, "febrero" to 2, "marzo" to 3, "abril" to 4, "mayo" to 5, "junio" to 6,
        "julio" to 7, "agosto" to 8, "septiembre" to 9, "octubre" to 10, "noviembre" to 11, "diciembre" to 12
    )
    
    val resultados = mutableListOf<DatoJus>()
    
    texto.lines().forEach { linea ->
        if (linea.isBlank() || linea.startsWith("MONTO") || linea.startsWith("Montos")) return@forEach
        
        val matches = regex.findAll(linea)
        matches.forEach { match ->
            val valorStr = match.groups[1]?.value?.replace(".", "")?.replace(",", ".")?.replace(" ", "") ?: return@forEach
            val fechaStr = match.groups[2]?.value?.lowercase() ?: return@forEach
            val valor = valorStr.toDoubleOrNull() ?: return@forEach
            
            val fecha = if (fechaStr.contains("de")) {
                val partes = fechaStr.split(" de ")
                if (partes.size == 3) {
                    val dia = partes[0].trim().toIntOrNull() ?: 1
                    val mes = meses[partes[1].trim()] ?: 1
                    val anio = partes[2].trim().toIntOrNull() ?: 2024
                    LocalDate(anio, mes, dia)
                } else null
            } else {
                try {
                    val separador = if (fechaStr.contains("/")) "/" else "."
                    val partes = fechaStr.split(separador)
                    if (partes.size == 3) {
                        val dia = partes[0].toInt()
                        val mes = partes[1].toInt()
                        var anio = partes[2].toInt()
                        if (anio < 100) anio += 2000
                        LocalDate(anio, mes, dia)
                    } else null
                } catch (e: Exception) { null }
            }
            
            if (fecha != null) {
                resultados.add(DatoJus(fecha, valor))
            }
        }
    }
    return resultados.sortedByDescending { it.fecha }
}

fun calcularConversionJus(
    cantidadJus: Double,
    fechaOrigen: LocalDate,
    datos: List<DatoJus>
): ResultadoJus? {
    if (datos.isEmpty()) return null
    
    // El valor actual es el más reciente de la lista
    val datoActual = datos.first()
    
    // Buscar el valor histórico más cercano (menor o igual a la fecha de origen)
    val datoHistorico = datos.filter { it.fecha <= fechaOrigen }.maxByOrNull { it.fecha } ?: datos.last()
    
    return ResultadoJus(
        cantidadJus = cantidadJus,
        valorUnitarioHistorico = datoHistorico.valor,
        valorUnitarioActual = datoActual.valor,
        montoHistoricoPesos = cantidadJus * datoHistorico.valor,
        montoActualizadoPesos = cantidadJus * datoActual.valor,
        fechaHistorica = datoHistorico.fecha,
        fechaActual = datoActual.fecha
    )
}

fun calcularConversionDesdePesos(
    montoPesos: Double,
    fechaOrigen: LocalDate,
    datos: List<DatoJus>
): ResultadoJus? {
    if (datos.isEmpty()) return null
    
    val datoActual = datos.first()
    val datoHistorico = datos.filter { it.fecha <= fechaOrigen }.maxByOrNull { it.fecha } ?: datos.last()
    
    val cantidadJus = if (datoHistorico.valor > 0) montoPesos / datoHistorico.valor else 0.0
    
    return ResultadoJus(
        cantidadJus = cantidadJus,
        valorUnitarioHistorico = datoHistorico.valor,
        valorUnitarioActual = datoActual.valor,
        montoHistoricoPesos = montoPesos,
        montoActualizadoPesos = cantidadJus * datoActual.valor,
        fechaHistorica = datoHistorico.fecha,
        fechaActual = datoActual.fecha
    )
}
